/**
 * Tabs of one browser share one session (9 Oct 2026).
 *
 * The refresh token rotates: every renewal retires the token it was given, and
 * the server reads a retired token presented again as a stolen copy and closes
 * every session the account has — every tab, every phone, the shared desk. Each
 * tab used to keep its own copy in memory, so the second tab to renew presented
 * the token the first had already spent, and the whole account was signed out.
 * Storage is the one copy all tabs share; these helpers read it.
 */

function claims(token: string): Record<string, unknown> | null {
  try {
    const part = token.split('.')[1];
    if (!part) return null;
    const json = atob(part.replace(/-/g, '+').replace(/_/g, '/'));
    const parsed: unknown = JSON.parse(json);
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

/** When an access token stops working, in milliseconds; 0 when it cannot be read. */
export function accessExpiry(token: string): number {
  const exp = claims(token)?.exp;
  return typeof exp === 'number' ? exp * 1000 : 0;
}

/** Whose access token this is; null when it cannot be read. */
export function accessSubject(token: string): string | null {
  const sub = claims(token)?.sub;
  return typeof sub === 'string' ? sub : null;
}

/**
 * Another tab renewed the session while this one waited: storage holds an
 * access token that is not the one that just failed, with time left on it.
 * Taking it spares a second renewal.
 */
export function renewedElsewhere(failed: string | null, stored: string | null, now = Date.now()): stored is string {
  return Boolean(stored && stored !== failed && accessExpiry(stored) > now + 30_000);
}
